/**
 * Compress image to File, strictly ≤ maxSizeKB.
 * Prefers WebP when the browser supports it (much clearer than JPEG at ≤5 KB).
 * Falls back to JPEG if WebP encoding is unavailable.
 */
export function compressImageToFile(
  file,
  maxSizeKB = 100,
  maxWidth = 800,
  maxHeight = 800,
  initialQuality = 0.6,
  preferredType = 'image/webp'
) {
  const maxBytes = maxSizeKB * 1024;
  if (maxSizeKB <= 5) {
    // Tiny budget: keep side modest so the image stays recognizable as a thumbnail
    maxWidth = Math.min(maxWidth, 220);
    maxHeight = Math.min(maxHeight, 220);
    initialQuality = Math.min(initialQuality, 0.72);
  } else if (maxSizeKB <= 20) {
    maxWidth = Math.min(maxWidth, 360);
    maxHeight = Math.min(maxHeight, 360);
    initialQuality = Math.min(initialQuality, 0.55);
  }

  const MIN_SIDE = 48;
  const QUALITY_STEP = 0.05;
  const MIN_QUALITY = 0.08;

  const supportsWebp =
    preferredType === 'image/webp' &&
    typeof document !== 'undefined' &&
    (() => {
      try {
        const c = document.createElement('canvas');
        c.width = 2;
        c.height = 2;
        return c.toDataURL('image/webp').startsWith('data:image/webp');
      } catch {
        return false;
      }
    })();

  const mime = supportsWebp ? 'image/webp' : 'image/jpeg';
  const ext = supportsWebp ? 'webp' : 'jpg';

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          } else {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        width = Math.max(MIN_SIDE, Math.round(width));
        height = Math.max(MIN_SIDE, Math.round(height));

        const outName = `${file.name.replace(/\.[^.]+$/, '') || 'photo'}.${ext}`;

        const tryRender = (w, h, q) => {
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }
              if (blob.size <= maxBytes) {
                resolve(
                  new File([blob], outName, {
                    type: mime,
                    lastModified: Date.now(),
                  })
                );
                return;
              }
              if (q > MIN_QUALITY + 0.01) {
                tryRender(w, h, Math.max(MIN_QUALITY, q - QUALITY_STEP));
                return;
              }
              if (w > MIN_SIDE && h > MIN_SIDE) {
                const nw = Math.max(MIN_SIDE, Math.floor(w * 0.82));
                const nh = Math.max(MIN_SIDE, Math.floor(h * 0.82));
                tryRender(nw, nh, Math.min(0.65, initialQuality));
                return;
              }
              reject(
                new Error(
                  `Could not compress to ${maxSizeKB} KB or less. Use a simpler image or lower resolution.`
                )
              );
            },
            mime,
            q
          );
        };

        tryRender(width, height, initialQuality);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
